
import { javaLib } from './javaLibDataMap'
import { Log } from '@fanfanlo'
import { IProject } from '../entities/IProject'

export type LibPathKey = keyof typeof javaLib

const fileLog = new Log(false, 'extractDependencies_f')
// fileLog.pause = true;
// fileLog.childrenPaused = true;
/**
 * 从 IProject 中提取所有使用的 Trigger 和 Action 的 type
 *
 * @param projectData - Project 数据对象
 * @returns 使用的类型集合
 *
 * @example
 * const types = extractUsedTypes(projectData)
 * // types: Set { "fanfanlo/triggers/time/timeout", "fanfanlo/actions/app/openApp" }
 */
export function extractUsedTypes(projectData: IProject): Set<string> {
    fileLog.log('[extractUsedTypes] 开始提取类型')
    const types = new Set<string>()

    fileLog.log('projectData=', JSON.stringify(projectData))

    // 修复：遍历 children 数组（TriggerActionGroup）
    const triggerActionGroups = projectData.children || []
    fileLog.log('[extractUsedTypes] triggerActionGroups 长度:', triggerActionGroups.length)

    for (let i = 0; i < triggerActionGroups.length; i++) {
        const triggerActionGroup = triggerActionGroups[i]
        fileLog.log('[extractUsedTypes] 处理 TriggerActionGroup', i, triggerActionGroup.ulid)

        // 遍历 TriggerAction
        const triggerActions = triggerActionGroup.children || []
        fileLog.log('[extractUsedTypes] triggerActions 长度:', triggerActions.length)

        for (let j = 0; j < triggerActions.length; j++) {
            const triggerAction = triggerActions[j]
            fileLog.log('[extractUsedTypes] 处理 TriggerAction', j, triggerAction.ulid)

            // 提取 Triggers
            const triggers = triggerAction.triggerGroup?.children || []
            fileLog.log('[extractUsedTypes] triggers 长度:', triggers.length)
            for (let k = 0; k < triggers.length; k++) {
                const trigger = triggers[k]
                fileLog.log('[extractUsedTypes] 处理 Trigger', k, 'type:', trigger.type)
                if (trigger.type) {
                    types.add(trigger.type)
                    fileLog.log('[extractUsedTypes] 添加 Trigger type:', trigger.type)
                } else {
                    fileLog.log('[extractUsedTypes] Trigger type 为空', k)
                }
            }

            // 提取 Actions
            const actions = triggerAction.actionGroup?.children || []
            fileLog.log('[extractUsedTypes] actions 长度:', actions.length)
            for (let k = 0; k < actions.length; k++) {
                const action = actions[k]
                fileLog.log('[extractUsedTypes] 处理 Action', k, 'type:', action.type)
                if (action.type) {
                    types.add(action.type)
                    fileLog.log('[extractUsedTypes] 添加 Action type:', action.type)
                } else {
                    fileLog.log('[extractUsedTypes] Action type 为空', k)
                }
            }
        }
    }

    fileLog.log('[extractUsedTypes] 提取完成, 总类型数:', types.size)
    fileLog.log('[extractUsedTypes] 类型列表:', Array.from(types))
    return types
}

/**
 * 递归收集依赖
 *
 * @param dependencies - 依赖列表
 * @param impSet - 已导入的依赖集合（避免重复）
 * @param impList - 导入代码列表
 * @returns 导入代码列表
 */
function prepareDependencies(
    dependencies: LibPathKey[],
    impSet: Set<LibPathKey> = new Set<LibPathKey>(),
    impList: string[] = []
): string[] {
    fileLog.log('[prepareDependencies] 处理依赖数量:', dependencies.length)

    for (let i = 0; i < dependencies.length; i++) {
        const path = dependencies[i]
        fileLog.log('[prepareDependencies] 处理依赖', i, 'path:', path)

        if (impSet.has(path)) {
            fileLog.log('[prepareDependencies] 依赖已存在,跳过:', path)
            continue
        }

        const file = javaLib[path]
        if (!file) {
            fileLog.log('[prepareDependencies] 错误: 找不到依赖:', path)
            fileLog.log('[prepareDependencies] 可用的 javaLib keys:', Object.keys(javaLib))
            throw new Error(`no path in javaLib, path is ${path}`)
        }

        fileLog.log('[prepareDependencies] 添加依赖:', path)
        impSet.add(path)
        impList.push(`"${path}":${file.content}`)

        if (file.dependencies && file.dependencies.length > 0) {
            fileLog.log('[prepareDependencies] 依赖有子依赖,数量:', file.dependencies.length)
            const list = file.dependencies.map(d => d.path) as LibPathKey[]
            prepareDependencies(list, impSet, impList)
        } else {
            fileLog.log('[prepareDependencies] 依赖无子依赖')
        }
    }

    fileLog.log('[prepareDependencies] 完成, 总依赖数:', impList.length)
    return impList
}

/**
 * 创建 javaLib 对象代码
 *
 * @param dependencies - 依赖列表
 * @returns javaLib 对象的 JavaScript 代码
 *
 * @example
 * const script = createLibScript(['fanfanlo/triggers/time/timeout'])
 * // script: "let javaLib = { ... }"
 */
export function createLibScript(dependencies: LibPathKey[]): string {
    fileLog.log('[createLibScript] 开始创建 javaLib 脚本')
    fileLog.log('[createLibScript] 输入依赖数量:', dependencies.length)

    // 强制包含的核心依赖
    const storePath = 'fanfanlo/javajs/v1/project/store' as LibPathKey
    const createEventDispatcherPath = 'fanfanlo/javajs/v1/event/createEventDispatcher' as LibPathKey
    const createGetterSetterPath = 'fanfanlo/javajs/v1/utils/createGetterSetter' as LibPathKey

    fileLog.log('[createLibScript] 添加强制依赖 - store:', storePath)
    fileLog.log('[createLibScript] 添加强制依赖 - createEventDispatcher:', createEventDispatcherPath)
    fileLog.log('[createLibScript] 添加强制依赖 - createGetterSetter:', createGetterSetterPath)

    // 合并所有依赖（强制依赖 + 项目依赖）
    const allDeps = [
        storePath,
        createEventDispatcherPath,
        createGetterSetterPath,
        ...dependencies
    ]
    fileLog.log('[createLibScript] 总依赖数量(含强制依赖):', allDeps.length)
    fileLog.log('[createLibScript] 强制依赖数量: 3 (store, createEventDispatcher, createGetterSetter)')
    fileLog.log('[createLibScript] 项目依赖数量:', dependencies.length)

    const list = prepareDependencies(allDeps)
    fileLog.log('[createLibScript] 收集到的代码片段数量:', list.length)

    const content = `let javaLib = {
        ${list.join(",")}
    }`

    fileLog.log('[createLibScript] javaLib 脚本创建完成')
    fileLog.log('[createLibScript] 脚本长度:', content.length)

    return content
}
